<script setup lang="ts">
import { computed, nextTick, onUnmounted } from "vue";
import ChatHistoryMessagesArea from "../components/chat/ChatHistoryMessagesArea.vue";
import { locale } from "../locale";
import {
  errorChatHistoryState,
  getHistoryMessages,
  historyId,
  historyMessagesList,
  loadingChatHistoryState,
  resetHistory,
} from "../states/chatHistoryState";
import { mounted, toBottomScrollChat } from "../states/scrollHistoryChatState";
import { waitLoading } from "../states/loadingMessageImage";
import LoadingIcon from "../components/chat/LoadingIcon.vue";
import ReloadIcon from "../components/chat/ReloadIcon.vue";
import { router } from "../router";
import { ws_instance } from "../websocket/ws-instance";
import { cleanup } from "../states/media/state";

ws_instance.disconnect()
cleanup()
const props = defineProps<{
  roomId: string;
}>();
historyId.value = props.roomId;
Promise.resolve()
  .then(() => {
    return getHistoryMessages();
  })
  .then(() => {
    nextTick(async () => {
      toBottomScrollChat();
      await Promise.all(
        historyMessagesList.value
          .filter((m) => m.type === "File" && m.content.file?.preview)
          .map((m) => {
            if ("id" in m) return waitLoading(m.id as string);
            else return Promise.resolve();
          }),
      );
      toBottomScrollChat();
      mounted.value = true;
    });
  });
onUnmounted(() => {
  resetHistory();
});
const isBack = computed(() => {
  return !!router.options.history.state.back
})
</script>
<template>
  <div class="chat-history-wrapper">
    <div class="room-chat">
      <div v-if="errorChatHistoryState.startMessages" class="error-start-messages">
        <div class="inner">
          <span v-if="errorChatHistoryState.denied">{{
            locale.chat.denied
            }}</span>
          <template v-else>
            <span>{{ locale.chat.errorGetStartMessages }}</span>
            <div class="loading" v-if="loadingChatHistoryState.startMessages">
              <LoadingIcon />
            </div>
            <div class="reload" v-else @click="getHistoryMessages">
              <ReloadIcon />
            </div>
          </template>
        </div>
      </div>
      <div class="title">
        <span class="text">{{ locale.chat.title }}</span>
        <div v-if="isBack" class="back" @click="router.back()">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 12L4 12M4 12L10.5 5.5M4 12L10.5 18.5" stroke="currentColor" stroke-width="1.5"
              stroke-linejoin="round" />
          </svg>
        </div>
      </div>
      <div class="room-chat-block">
        <div class="chat-area">
          <ChatHistoryMessagesArea />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.chat-history-wrapper {
  width: 100vw;
  height: 100%;
  height: 100dvh;
  background-color: #1e1818;
  background-image: var(--bg-image);
  background-repeat: repeat;
  display: flex;
  justify-content: center;
  padding: 12px;
}

.room-chat {
  width: 450px;
  height: 100%;
  background-color: #1e1818;
  border: 1px solid #494949;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  border-radius: 8px;
}

.title {
  height: 40px;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #332f2f;
  color: #f6f6f6;
  flex-shrink: 0;
}

.title .text {
  font: Open Sans;
  font-size: 14px;
  font-weight: 600;
}

.room-chat-block {
  height: 100%;
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.chat-area {
  width: 100%;
  height: 100px;
  flex-grow: 1;
  position: relative;
}

.error-start-messages {
  position: absolute;
  z-index: 100;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background-color: rgba(30, 24, 24, 0.9);
  backdrop-filter: blur(4px);
}

.error-start-messages .inner {
  border-radius: 8px;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 14px;
}

.reload {
  cursor: pointer;
}

.back {
  position: absolute;
  top: 8px;
  left: 8px;
  cursor: pointer;
  color: #a4a4a4;
}

.back:hover {
  color: #d8d8d8;
}
</style>
